/**
 * Состав партии под раскрытой строкой: размеры, статус строки, правка плана на
 * месте, склады, короба, уходимость. Вынесено из `components/PlansTable.tsx`
 * (нарезка 14.10.26); статус и комментарии строки — спека
 * `docs/specs/2026-11-04-plan-line-status-design.md`.
 */

import { useCallback, useState } from "react";
import { QtyInput } from "@/components/sizeCells";
import { ApiFailure, apiFetch } from "@/lib/api";
import { nf } from "@/lib/format";
import { Badge } from "./Badge";
import { fmtMoney, speedTitle, stockTitle } from "./format";
import { CommentCount } from "./PlanComments";
import { LINE_STATUS_TONE } from "./style";
import type { LineSaved, PlanComment, PlanLine, PlanTotalsPatch } from "./types";

/** Отменённая и пересорт: их не ждём — строка приглушена, план зачёркнут. */
function lineIsOff(line: Pick<PlanLine, "status_id">): boolean {
  return line.status_id === 4 || line.status_id === 5;
}

/** Почему число строки не правится из-за её статуса; `null` — правится. Сервер
 *  откажет тем же (`decide_line_qty`), поле знает это до набора. */
function qtyLock(line: Pick<PlanLine, "status_id">): string | null {
  if (line.status_id === 2 || line.status_id === 3) return "строка в заказе или отгружена — число не правим";
  if (line.status_id === 5) return "пересорт — верните строку в «Активно» (клик по статусу), тогда число правится";
  return null;
}

export function LinesBlock({
  rows,
  failure,
  onRetry,
  windowDays,
  writeBlocked,
  onSaved,
  batchComments,
  onOpenLine,
}: {
  rows: PlanLine[] | undefined;
  /** Состав не приехал — почему, словами; повтор — `onRetry`. */
  failure?: string | undefined;
  onRetry: () => void;
  /** Окно уходимости: все четыре приехали в ответе, выбор идёт без запроса. */
  windowDays: number;
  /** Почему правка закрыта, СЛОВАМИ; `null` — открыта. Поле обязано знать это
   *  до набора, а не по 409 после: набранное иначе выглядит записанным. */
  writeBlocked: string | null;
  /** Успешная правка: новая строка и новые итоги партии — экран правит ими
   *  строку списка, не перезапрашивая все 20 000 партий. */
  onSaved: (line: PlanLine, totals: PlanTotalsPatch) => void;
  /** Комментарии areserv к партии — строкой над составом, целиком в карточке строки. */
  batchComments: PlanComment[];
  /** Клик по статусу или значку комментариев — карточка строки справа. */
  onOpenLine: (line: PlanLine) => void;
}) {
  // Какая СТРОКА сейчас пишется, а не флаг «что-то пишется»: соседние размеры
  // остаются живыми, закрыто только то поле, чьё число уехало.
  const [saving, setSaving] = useState<number | null>(null);
  const [failed, setFailed] = useState<{ id: number; text: string } | null>(null);
  // Счётчик перерисовок поля: `QtyInput` перечитывает `value`, только когда
  // оно ИЗМЕНИЛОСЬ, а после отказа число возвращается ПРЕЖНЕЕ — и без
  // пересоздания в поле осталось бы набранное, которого в CRM нет.
  const [resets, setResets] = useState<Record<number, number>>({});

  const save = useCallback(
    async (line: PlanLine, raw: string) => {
      const qty = Number(raw);
      if (raw === "" || !Number.isInteger(qty) || qty < 0) {
        setFailed({ id: line.id, text: "нужно целое число от нуля" });
        setResets((prev) => ({ ...prev, [line.id]: (prev[line.id] ?? 0) + 1 }));
        return;
      }
      if (qty === line.qty_planned) return;
      setSaving(line.id);
      setFailed(null);
      try {
        const done = await apiFetch<LineSaved>(`/api/plans/lines/${line.id}`, {
          method: "POST",
          body: JSON.stringify({ qty }),
        });
        // Сумму строки сервер не отдаёт — считаем сами от цены строки, иначе
        // «план 20, сумма за 10» спорила бы с уже новым итогом партии.
        const price = line.price ? Number(line.price) : 0;
        const amount = price > 0 ? String(Math.round(done.qty * price * 100) / 100) : line.amount;
        onSaved({ ...line, qty_planned: done.qty, status_id: done.status_id, amount }, done.totals);
      } catch (error) {
        setFailed({
          id: line.id,
          text: error instanceof ApiFailure ? error.message : "CRM не ответила",
        });
        setResets((prev) => ({ ...prev, [line.id]: (prev[line.id] ?? 0) + 1 }));
      } finally {
        setSaving(null);
      }
    },
    [onSaved],
  );

  if (!rows)
    return failure ? (
      <div className="px-9 py-2 text-[12.5px] text-err">
        Состав не приехал: {failure}{" "}
        <button type="button" onClick={onRetry} className="text-accent-text hover:underline">
          ещё раз
        </button>
      </div>
    ) : (
      <div className="px-9 py-2 text-[12.5px] text-fg-muted">
        Загружаем состав…
      </div>
    );
  if (rows.length === 0)
    return (
      <div className="px-9 py-2 text-[12.5px] text-fg-muted">
        В этой партии нет строк с количеством больше нуля.
      </div>
    );
  const lastBatch = batchComments[0];
  return (
    <div className="bg-bg-subtle px-9 py-2">
      {/* Одной строкой фиксированной высоты: высоту блока виртуализатор берёт
          из оценки (`PlansTable`), и лента комментариев её бы разорвала. */}
      {lastBatch ? (
        <div
          className="h-[22px] truncate text-[11.5px] text-fg-muted"
          title={batchComments.map((c) => `${c.user ?? ""}: ${c.text}`).join("\n")}
          data-annot="комментарии партии areserv"
        >
          <span className="text-fg-faint">areserv · комментариев партии {batchComments.length}:</span>{" "}
          {lastBatch.text}
        </div>
      ) : null}
      <table className="w-full overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg text-[12.5px]">
        <thead>
          <tr className="text-[11px] text-fg-faint">
            <th className="h-7 px-2.5 text-left font-medium">размер</th>
            <th className="h-7 px-2.5 text-left font-medium">статус</th>
            <th className="h-7 px-2.5 text-left font-medium">бирка</th>
            <th className="h-7 px-2.5 text-right font-medium">план</th>
            <th className="h-7 px-2.5 text-right font-medium">в заказе</th>
            <th className="h-7 px-2.5 text-right font-medium">цена ¥</th>
            <th className="h-7 px-2.5 text-right font-medium">сумма ¥</th>
            <th className="h-7 px-2.5 text-left font-medium">свойство</th>
            <th className="h-7 px-2.5 text-right font-medium">склад МП</th>
            <th className="h-7 px-2.5 text-right font-medium">короба</th>
            <th className="h-7 px-2.5 text-right font-medium">уходимость</th>
            <th className="h-7 px-2.5 text-left font-medium">заказ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((line) => (
            <tr
              key={line.id}
              className={
                "border-t border-border" +
                // Приглушены все ячейки, кроме статуса: им строку и возвращают.
                (lineIsOff(line) ? " [&>td:not([data-live])]:opacity-50" : "")
              }
            >
              <td className="h-[30px] whitespace-nowrap px-2.5">
                <span className="tnum">{line.size ?? "—"}</span>
                {line.size_name ? (
                  <span className="ml-1.5 text-fg-faint">{line.size_name}</span>
                ) : null}
              </td>
              <td data-live className="h-[30px] whitespace-nowrap px-2.5">
                <span className="inline-flex items-center gap-1.5">
                  <button
                    type="button"
                    title="статус и комментарии строки"
                    onClick={() => onOpenLine(line)}
                    className="cursor-pointer rounded-full hover:ring-1 hover:ring-accent"
                  >
                    <Badge tone={LINE_STATUS_TONE[line.status_id]} text={line.status_name ?? "—"} />
                  </button>
                  <CommentCount items={line.comments} onClick={() => onOpenLine(line)} />
                </span>
              </td>
              <td className="h-[30px] px-2.5 text-fg-muted">
                {line.size_rf ?? "—"}
              </td>
              {/* План правится ПРЯМО ЗДЕСЬ, и правка уходит по выходу из
                  поля и по Enter (решение владельца 16.09.26). Цена названа:
                  каждый размер — отдельная запись в чужую боевую базу, поэтому
                  поле на время записи закрыто, а отказ возвращает прежнее
                  число и встаёт словами под строкой. */}
              <td className="tnum h-[30px] px-2.5 text-right">
                <QtyInput
                  key={`${line.id}:${resets[line.id] ?? 0}`}
                  value={line.qty_planned}
                  disabled={Boolean(writeBlocked) || Boolean(qtyLock(line)) || saving === line.id}
                  struck={lineIsOff(line)}
                  reason={qtyLock(line) ?? undefined}
                  broken={
                    writeBlocked ??
                    (failed?.id === line.id ? failed.text : undefined)
                  }
                  onCommit={(raw) => void save(line, raw)}
                />
              </td>
              <td
                className={
                  "tnum h-[30px] px-2.5 text-right " +
                  // Тот же страж, что у колонки партии: 0 ≥ 0 — не «заказано».
                  (line.qty_planned > 0 && line.qty_ordered >= line.qty_planned ? "text-ok" : "")
                }
              >
                {nf.format(line.qty_ordered)}
              </td>
              <td className="tnum h-[30px] px-2.5 text-right">
                {/* Строка без цены помечается словом, а не пустотой: пустая
                    ячейка читается как «ноль», а это разные вещи. */}
                {line.price && Number(line.price) > 0 ? (
                  fmtMoney(line.price)
                ) : (
                  <span className="font-medium text-err">нет цены</span>
                )}
              </td>
              <td className="tnum h-[30px] px-2.5 text-right">
                {fmtMoney(line.amount)}
              </td>
              <td className="h-[30px] max-w-[220px] truncate px-2.5 text-fg-muted">
                {line.property_name ?? (
                  <span className="text-err">нет свойства</span>
                )}
              </td>
              {/* Склад двух площадок ОДНИМ числом, слагаемые в подсказке:
                  сложены два разных основания, и человек должен видеть, чего
                  сколько. Прочерк — снимка нет вовсе, ноль — склад пуст. */}
              <td
                className="tnum h-[30px] px-2.5 text-right text-fg-muted"
                title={stockTitle(line.stock)}
              >
                {line.stock.mp ?? "—"}
              </td>
              {/* Ноль коробов печатается НУЛЁМ, приглушённо, — как на
                  карточке товара (`KorobCell`): короба считает наша же CRM, и
                  «пусто» тут честный ответ, а не «источника нет». Прочерк
                  остаётся привилегией снимков площадок. */}
              <td
                className="tnum h-[30px] px-2.5 text-right text-fg-muted"
                title="в наших коробах — то же условие отбора, что на «Остатках» кабинета WB"
              >
                {line.korob ? (
                  nf.format(line.korob)
                ) : (
                  <span className="text-fg-faint">0</span>
                )}
              </td>
              <td
                className="tnum h-[30px] px-2.5 text-right text-fg-muted"
                title={speedTitle(line.speed, windowDays)}
              >
                {line.speed?.[String(windowDays)]?.net ?? "—"}
              </td>
              <td className="h-[30px] px-2.5 font-mono text-[11px] text-fg-muted">
                {line.supply_order_ids ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {failed ? (
        <div className="px-1 pt-1 text-[11.5px] text-err">{failed.text}</div>
      ) : null}
    </div>
  );
}
